// FAQs generated alongside product descriptions. They are kept apart from the
// description in the app and appended to it, under a heading, when copied or pushed.

export interface ProductFaq {
  question: string;
  answer: string;
}

// Keyed by the description language values used in the apps' language menus.
const FAQ_HEADINGS: Record<string, string> = {
  english: 'Frequently Asked Questions',
  spanish: 'Preguntas frecuentes',
  french: 'Questions fréquentes',
  german: 'Häufig gestellte Fragen',
  italian: 'Domande frequenti',
  portuguese: 'Perguntas frequentes',
  dutch: 'Veelgestelde vragen',
  chinese: '常见问题',
  japanese: 'よくある質問',
  korean: '자주 묻는 질문',
  arabic: 'الأسئلة الشائعة',
  hindi: 'अक्सर पूछे जाने वाले प्रश्न',
  russian: 'Часто задаваемые вопросы',
  turkish: 'Sıkça Sorulan Sorular',
};

// The FAQ heading in the description's language. Unknown or missing languages
// (e.g. results saved before language was stored) fall back to English.
export function faqHeadingFor(language: unknown): string {
  return (typeof language === 'string' && FAQ_HEADINGS[language.toLowerCase()]) || FAQ_HEADINGS.english;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// The Q&A pairs as editable HTML, without the section heading.
export function faqItemsToHtml(faqs: ProductFaq[] | undefined): string {
  return (faqs || [])
    .filter(faq => faq.question.trim() && faq.answer.trim())
    // Questions as headings so search engines and AI assistants can pair each question with its answer.
    .map(faq => `<h3>${escapeHtml(faq.question.trim())}</h3><p>${escapeHtml(faq.answer.trim())}</p>`)
    .join('');
}

// Plain text of an HTML block, keeping line and paragraph breaks.
export function htmlToPlainText(html: string | undefined): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|h[1-6]|li)>/gi, '\n\n');
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

// Adds the FAQ block below the description, under a heading in the description's
// language. Empty blocks add nothing.
export function appendFaqSection(descriptionHtml: string, faqItemsHtml: string | undefined, language?: unknown): string {
  if (!faqItemsHtml || !htmlToPlainText(faqItemsHtml)) return descriptionHtml;
  return `${descriptionHtml}<h2>${faqHeadingFor(language)}</h2>${faqItemsHtml}`;
}

// A saved result's FAQ block: the user's edited HTML if they changed it, otherwise the generated list.
export function faqItemsHtmlFor(result: { faqs?: ProductFaq[]; faqsHtml?: string }): string {
  return result.faqsHtml ?? faqItemsToHtml(result.faqs);
}
